import React, { useEffect } from 'react';
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "../authConfig";
import { ShieldCheck, Lock } from 'lucide-react';

export function Login({ error }) {
  const { instance, inProgress, accounts } = useMsal();
  // Only show "Authenticating" if there is no error yet. 
  // If there's an error, we want to show the login button again so they can try a different account.
  const isLoggingIn = inProgress !== InteractionStatus.None || (accounts.length > 0 && !error);

  const handleLogin = () => {
    if (isLoggingIn) return;
    instance.loginRedirect(loginRequest).catch(e => {
      console.error(e);
    });
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <div className="company-logo-badge">
            <img
              src="https://wissda.com/assets/brand/wissda-logo-on-dark.svg"
              alt="Wissda Logo"
              className="company-logo"
            />
          </div>
          <div className="divider"></div>
          <div className="platform-info">
            <h1>Compliance Center</h1>
            <p>ISO 27001 GRC Management Platform</p>
          </div>
        </div>

        <div className="login-body">
          <div className="welcome-section">
            <h2>Welcome Back</h2>
            <p>Please sign in with your corporate account to access the platform.</p>
          </div>

          {error && (
            <div className="auth-error">
              <ShieldCheck size={18} />
              <span>{error}</span>
            </div>
          )}

          <button 
            className={`btn btn-login ${isLoggingIn ? 'loading' : ''}`} 
            onClick={handleLogin}
            disabled={isLoggingIn}
          >
            {isLoggingIn ? (
              <span>Authenticating...</span>
            ) : (
              <>
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
                  alt="Microsoft"
                  className="ms-logo"
                />
                Sign in with Microsoft Entra ID
              </>
            )}
          </button>
        </div>

        <div className="login-footer">
          <div className="security-badge">
            <Lock size={14} />
            <span>Secure Enterprise Login</span>
          </div>
          <p>© {new Date().getFullYear()} Wissda. All rights reserved.</p>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        .login-page {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: radial-gradient(ellipse at 50% 25%, #1e293b 0%, #0a0e1a 100%);
          z-index: 9999;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }

        .login-card {
          width: 100%;
          max-width: 440px;
          background: #0f172a;
          border-radius: 24px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 35px rgba(29, 126, 182, 0.2);
          padding: 40px;
          text-align: center;
          border: 1px solid #1e293b;
        }

        .login-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
          margin-bottom: 32px;
        }

        .company-logo-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 10px 24px;
          background: #020617;
          border: 1px solid #1e293b;
          border-radius: 14px;
          box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.05), 0 4px 14px rgba(0, 0, 0, 0.4);
        }

        .company-logo {
          height: 44px;
          width: auto;
          display: block;
        }

        .divider {
          width: 40px;
          height: 2px;
          background: #1e293b;
          border-radius: 1px;
        }

        .platform-info h1 {
          font-size: 1.5rem;
          font-weight: 800;
          color: #f8fafc;
          margin: 0;
          letter-spacing: -0.025em;
        }

        .platform-info p {
          font-size: 0.875rem;
          color: #94a3b8;
          margin: 4px 0 0;
        }

        .welcome-section {
          margin-bottom: 32px;
        }

        .welcome-section h2 {
          font-size: 1.25rem;
          font-weight: 700;
          color: #f1f5f9;
          margin: 0 0 8px;
        }

        .welcome-section p {
          font-size: 0.9375rem;
          color: #94a3b8;
          line-height: 1.5;
        }

        .btn-login {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 14px;
          background: #1d7eb6;
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 4px 12px rgba(29, 126, 182, 0.35);
        }

        .btn-login:hover {
          background: #176694;
          transform: translateY(-1px);
          box-shadow: 0 8px 20px rgba(29, 126, 182, 0.45);
        }

        .btn-login:active {
          transform: translateY(0);
        }

        .ms-logo {
          width: 20px;
          height: 20px;
        }

        .auth-error {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid rgba(239, 68, 68, 0.3);
          color: #fca5a5;
          padding: 12px;
          border-radius: 12px;
          margin-bottom: 24px;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .login-footer {
          margin-top: 40px;
          padding-top: 24px;
          border-top: 1px solid #1e293b;
        }

        .security-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid #1e293b;
          padding: 6px 12px;
          border-radius: 20px;
          color: #94a3b8;
          font-size: 0.75rem;
          font-weight: 600;
          margin-bottom: 12px;
        }

        .login-footer p {
          font-size: 0.75rem;
          color: #64748b;
          margin: 0;
        }
      `}} />
    </div>
  );
}
